<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import HeaderBackButton from '@/components/Layout/Components/Header/HeaderBackButton.vue';
import HeaderChatBox from '@/components/Layout/Components/Header/HeaderChatBox.vue';
import HeaderHelpCenter from '@/components/Layout/Components/Header/HeaderHelpCenter.vue';
import HeaderLogo from '@/components/Layout/Components/Header/HeaderLogo.vue';
import HeaderProgressBar from '@/components/Layout/Components/Header/HeaderProgressBar.vue';
import HeaderUspSection from '@/components/Layout/Components/Header/HeaderUspSection.vue';
import { useRoutingButtonActions } from '@/composables/useRoutingButtonActions.js';
import { formStore } from '@/stores/form.js';

const props = defineProps({
  disabledUspSection: {
    type: Boolean,
    default: false,
  },
  disabledChatBox: {
    type: Boolean,
    default: false,
  },
  disabledHelpCenter: {
    type: Boolean,
    default: false,
  },
});

const { currentRouteIndex, totalRoutes, backButtonVisible, backButtonAction } = useRoutingButtonActions();
const { tm } = useI18n();
const form = formStore();

const updatedUsps = computed(() => {
  if (props.disabledUspSection) return [];
  let usps = tm('general.header.usps');

  // Replace the placeholder with the actual percentage from the store
  usps[1] = usps[1].replace('{percentage}', form.ktv.percentage_aanbevelingen);

  return usps;
});

// eslint-disable-next-line no-undef
const baseUrl = publicPath + modulePath || import.meta.env.VITE_BASE_URL;

const logo = computed(() => {
  return `${baseUrl}/logo/${form.tenant}_logo.png`;
});
</script>

<template>
  <header class="header">
    <div class="header__top shadow">
      <!-- USP section (desktop only) -->
      <div
        v-if="!disabledUspSection"
        class="container-fluid d-none d-lg-block"
      >
        <div class="row">
          <HeaderUspSection :usps="updatedUsps" />
        </div>
      </div>
      <!-- Header bar -->
      <div class="container-lg">
        <div class="row pt-3 pb-3">
          <div class="col-12 d-flex align-items-center justify-content-between flex-wrap">
            <div class="d-flex align-items-center d-lg-none">
              <HeaderBackButton
                v-if="backButtonVisible"
                @on-click="backButtonAction"
              />
            </div>

            <div class="d-flex align-items-center">
              <HeaderLogo :src="logo" />
            </div>

            <div class="header__buttons d-flex align-items-center justify-content-end">
              <!-- Chat icon -->
              <div v-if="!disabledChatBox">
                <HeaderChatBox />
              </div>
              <!-- Help icon -->
              <div v-if="!disabledHelpCenter">
                <HeaderHelpCenter />
              </div>
            </div>

            <!-- Progressbar -->
            <div class="col-12 col-lg-8 d-flex align-items-center justify-content-center">
              <HeaderProgressBar
                :total="totalRoutes"
                :current="currentRouteIndex + 1"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
  <!-- Back button (desktop only) -->
  <div class="container-lg d-none d-lg-block pt-4">
    <HeaderBackButton
      v-if="backButtonVisible"
      @on-click="backButtonAction"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '~bootstrap/scss/functions';
@import '~bootstrap/scss/variables';
@import '~bootstrap/scss/mixins';

.header {
  position: sticky;
  z-index: 999;
  top: 0;
  width: 100%;

  &__top {
    background-color: var(--ux-colors-white);
    border-radius: 0 0 1.6rem 1.6rem;
  }

  &__buttons {
    margin-left: auto;

    @include media-breakpoint-up(lg) {
      margin-left: 0;
      order: 4;
    }
  }
}
</style>
